import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { cloneDeep } from "lodash";

import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, skip, tap } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, getLoadingPartyState, loadedSupplierPartyListState, loadedTenantState } from "src/app/store/reducers";
import { getSupplierPartyList } from "src/app/store/actions/parties/parties.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { ProductSuppliersService } from "src/app/shared/services/products/productSuppliers/productSuppliers.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { ProductSupplierDTO } from "src/app/models/Products/productSupplierDTO";
import { DefaultConfirmationDialogData, Dialog } from "src/app/models/dialog/dialog.model";
import { AutocompleteSelectItem } from "src/app/models/selection-list/autocomplete-select-item.model";
import { SupplierPartyDTO } from "src/app/models/Party/supplierPartyDTO";
import { FormUtils } from "src/app/shared/utils/formUtils";

import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { CHIP_STYLE, getChipStyle } from "src/app/shared/utils/customTableUtils";

//Receipt Detail Form
export const FORM_PARAMS = {
  partySupplier: "partySupplier",
  productSupplierRefCode: "productSupplierRefCode",
  defaultSupplier: "defaultSupplier",
  minBuyQuantity: "minBuyQuantity",
  lastBuyAmount: "lastBuyAmount",
  lastBuyDate: "lastBuyDate",
};

@Component({
  selector: "app-product-suppliers-dialog",
  templateUrl: "./product-suppliers-dialog.component.html",
  styleUrls: ["./product-suppliers-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ProductSuppliersDialogComponent implements OnInit, OnDestroy {
  isLoading$ = new BehaviorSubject<boolean>(false);

  productSuppliersDataForm: FormGroup;

  currentProductSupplier: ProductSupplierDTO;

  isCreate: boolean;

  //Tenant Data
  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  dateTimeFormat: string;

  //Suppliers List
  isLoadingSupplier$: Observable<boolean>;
  suppliersList$: Observable<SupplierPartyDTO[]>;
  suppliersList: AutocompleteSelectItem[] = [];
  suppliersSub: Subscription;
  searchTimer: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ProductSuppliersDialogComponent>,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private productSuppliersService: ProductSuppliersService
  ) {
    this.tenantData$ = this.store.pipe(select(loadedTenantState));
    this.suppliersList$ = this.store.pipe(select(loadedSupplierPartyListState));
    this.isLoadingSupplier$ = this.store.pipe(select(getLoadingPartyState));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.loadTenantDateTimeFomat();
    this.createProductSupplierForm();

    this.validateMode();

    //Search subscriptions
    this.setSupplierSearchSubscription();
  }

  loadTenantDateTimeFomat(): void {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.dateTimeFormat = data.datetimeFormat;
      }
    });
  }

  validateMode() {
    if (this.isCreate && this.data.rowData) {
      this.duplicate();
      return;
    }

    if (this.isCreate) {
      return;
    }

    this.getProductSupplierInfo(this.data.productCode, this.data.rowData.partySupplierCode);
  }

  setDialogData() {
    this.currentProductSupplier = this.data.rowData;
    this.isCreate = this.data.isCreate;
  }
  
  createProductSupplierForm() {
    this.productSuppliersDataForm = this.fb.group({
      [FORM_PARAMS.partySupplier]: [
        null,
        [
          Validators.required,
          FormUtils.noWhitespaceValidator,
          FormUtils.isObjectValidator,
        ],
      ],
      [FORM_PARAMS.productSupplierRefCode]: [null, Validators.required],
      [FORM_PARAMS.defaultSupplier]: [false, Validators.required],
      [FORM_PARAMS.minBuyQuantity]: [null, Validators.max(999999.999)],
      [FORM_PARAMS.lastBuyAmount]: { value: null, disabled: true },
      [FORM_PARAMS.lastBuyDate]: { value: null, disabled: true },
    });
  }

  getProductSupplierInfo(productCode: string, supplierCode: string) {
    this.isLoading$.next(true);
    this.productSuppliersService
      .getProductSupplier(productCode, supplierCode)
      .pipe(
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Products.productSupplierLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((productSupplier) => {
        this.currentProductSupplier = cloneDeep(productSupplier);
        this.setFormValues(productSupplier);
      });
  }

  setSupplierSearchSubscription() {
    this.suppliersSub = this.suppliersList$
      .pipe(skip(1))
      .subscribe((suppliers) => {
        if (suppliers) {
          this.suppliersList = suppliers.map(
            ({ code, supplierCode, name, statusCode, statusDescription }) => {
              return {
                code: code,
                displayCode: supplierCode ?? "",
                description: name ?? "",
                statusCode: statusCode ?? "",
                statusDescription: statusDescription ?? "",
              };
            }
          );
        }
      });
  }

  validateFirstSupplierSearch(event: KeyboardEvent) {
    if (!this.suppliersList.length) {
      this.searchSuppliers(event, false);
    }
  }

  searchSuppliers(event: KeyboardEvent, hasTimer = true) {
    const { value: searchTerm } = event.target as HTMLInputElement;

    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(
      () => {
        this.store.dispatch(
          getSupplierPartyList({
            options: {
              filteringArgs: {
                filterConfig: [{ field: "supplierCode" }, { field: "name" }],
                searchTerms: [searchTerm],
              },
            },
          })
        );
      },
      hasTimer ? 500 : 0
    );
  }

  displayAutocompleteOption(option: {
    code: string;
    displayCode: string;
    description: string;
  }): string {
    return option?.description ?? "";
  }

  getBasicListStatusChip(statusCode: string) {
    const styleSettings = this.setBasicChipStyle(statusCode);
    return styleSettings;
  }

  setBasicChipStyle(statusCode: string) {
    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
    switch (statusCode) {
      case "ACTIVE":
        style = CHIP_STYLE.SUCCESS;
        break;
      case "DISABLED":
        style = CHIP_STYLE.INACTIVE;
        break;
      case "BLOCKED":
        style = CHIP_STYLE.DANGER;
        break;
    }
    return getChipStyle(style);
  }

  openSupplier() {
    const supplierCode =
      this.productSuppliersDataForm.controls[FORM_PARAMS.partySupplier]?.value
        ?.code;
    if (supplierCode) {
      return ["/suppliers/supplier", supplierCode];
    }
  }

  setFormValues(productSupplier: ProductSupplierDTO, duplication?: boolean) {
    this.productSuppliersDataForm.controls.defaultSupplier.setValue(
      productSupplier?.defaultSupplier ?? false
    );
    this.productSuppliersDataForm.controls.partySupplier.setValue(
      !productSupplier.partySupplierCode || duplication
        ? null
        : {
          code: productSupplier.partySupplierCode,
          displayCode: productSupplier.partySupplierCode,
          description: productSupplier.partySupplierName,
        }
    );
    this.productSuppliersDataForm.controls.productSupplierRefCode.setValue(
      duplication ? null : productSupplier?.productSupplierRefCode ?? null
    );
    this.productSuppliersDataForm.controls.minBuyQuantity.setValue(
      productSupplier?.minBuyQuantity ?? null
    );
    this.productSuppliersDataForm.controls.lastBuyAmount.setValue(
      productSupplier?.lastBuyAmount ?? null
    );
    this.setDateField(this.productSuppliersDataForm, 'lastBuyDate', duplication);
  }

  setDateField(productSupplierForm: FormGroup, fieldParam: string, duplication?: boolean) {
    productSupplierForm.controls[FORM_PARAMS[fieldParam]].setValue(
      duplication
        ? null
        : FormUtils.setGivenDateTimePickerFormat(
            this.currentProductSupplier[fieldParam],
            this.dateTimeFormat ?? "DD/MM/YYYY HH:mm:ss"
          ) ?? null
    );
  }

  openDeleteProductSupplierDialog() {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteProductSupplier(this.currentProductSupplier.productCode, this.currentProductSupplier.partySupplierCode);
      }
    });
  }

  duplicate() {
    this.setFormValues(this.currentProductSupplier, true);

    this.isCreate = true;
    this.currentProductSupplier = null;
  }

  onSubmit() {
    if (!this.productSuppliersDataForm.valid) {
      this.productSuppliersDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("Products.FormInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (this.productSuppliersDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("Products.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    let productSupplier: ProductSupplierDTO = {
      productCode: this.data.productCode,
      partySupplierCode: this.productSuppliersDataForm.controls.partySupplier.value.displayCode ?? null,
      defaultSupplier: this.productSuppliersDataForm.controls.defaultSupplier.value ?? false,
      productSupplierRefCode: this.productSuppliersDataForm.controls.productSupplierRefCode.value ?? null,
      minBuyQuantity: this.productSuppliersDataForm.controls.minBuyQuantity.value ?? null,
      token: this.currentProductSupplier?.token ?? null,
    };

    if (this.isCreate) {
      this.createProductSupplier(productSupplier);
    } else {
      this.updateProductSupplier(productSupplier);
    }
  }

  createProductSupplier(productSupplier: ProductSupplierDTO) {
    this.isLoading$.next(true);
    this.productSuppliersService
      .createProductSupplier(productSupplier)
      .pipe(
        tap((createdProductSupplier) => {
          this.snackbarService.show({
            message: this.translate.instant("Products.productSupplierCreated"),
            type: SnackbarType.SUCCESS,
          });

          this.isCreate = false;
          this.currentProductSupplier = cloneDeep(createdProductSupplier);

          this.setFormValues(createdProductSupplier);

          this.dialogRef.close(true);
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant(error) ?? this.translate.instant("Products.productSupplierCreateFail"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  updateProductSupplier(productSupplier: ProductSupplierDTO) {
    this.isLoading$.next(true);
    this.productSuppliersService
      .updateProductSupplier(productSupplier)
      .pipe(
        tap((updatedProductSupplier) => {
          this.snackbarService.show({
            message: this.translate.instant("Products.productSupplierUpdated"),
            type: SnackbarType.SUCCESS,
          });

          this.currentProductSupplier = cloneDeep(updatedProductSupplier);

          this.setFormValues(updatedProductSupplier);

          this.dialogRef.close(true);
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant(error) ?? this.translate.instant("Products.productSupplierUpdateFail"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  deleteProductSupplier(productCode: string, supplierCode: string) {
    this.isLoading$.next(true);
    this.productSuppliersService
      .deleteProductSupplier(productCode, supplierCode)
      .pipe(
        tap(() => {
          this.snackbarService.show({
            message: this.translate.instant("Products.productSupplierDeleted"),
            type: SnackbarType.SUCCESS,
          });
          this.dialogRef.close(true);
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Products.productSupplierDeleteFail"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  close() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    if (this.suppliersSub) {
      this.suppliersSub.unsubscribe();
    }
    if (this.tenantDataSub) {
      this.tenantDataSub.unsubscribe();
    }
  }
}
